import React from 'react';
import { Product, ProductUsage } from '../types';
import { useUnits, useFormulations, useIngredients } from '../helpers/hooks';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import { useQueryRetrieveProductIngredients } from '../services/stores/queries';

interface ProductDetailsProps {
  product: Product;
}

export const ProductDetails: React.FC<ProductDetailsProps> = ({ product }) => {
  const { toUnitName } = useUnits();
  const { toFormulationName, toFormulationLongName } = useFormulations();
  const { toIngredientName, toIngredientLongName } = useIngredients();

  const {data: ingredients, 
         error: errorIngredients, 
         isLoading: isLoadingIngredients} = useQueryRetrieveProductIngredients(product.productID);


  return (
    <Card className="shadow-sm border-0 rounded-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-4">
          <Badge bg="primary" className="rounded-pill">
            {product.toxicologyGroup || 'No Group'}
          </Badge>
        </div>

        <Row className="g-4">
          <Col md={6}>
            <div className="mb-4">
              <h5 className="text-primary mb-3">Categories</h5>
              <div className="d-flex flex-wrap gap-2">
                {product.categories?.map((category, index) => (
                  <Badge key={index} bg="light" text="dark" className="rounded-pill px-3 py-2">
                    {category.categoryID}
                  </Badge>
                )) || 'N/A'}
              </div>
            </div>

            <div className="mb-4">
              <h5 className="text-primary mb-3">Formulation</h5>
              <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                {toFormulationName(product.formulationID) || ''}
              </Badge>
              {toFormulationLongName(product.formulationID) || ''}
            </div>

            <div className="mb-4">
              <h5 className="text-primary mb-3">Matieres actives</h5>
              {ingredients && ingredients.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {ingredients.map((ingredient, index) => (
                        <div key={ingredient.ingredientID} className="d-flex justify-content-between align-items-center">
                          <span className="fw-medium">{toIngredientLongName(ingredient.ingredientID)}</span>
                          <span>
                            {ingredient.concentration} {toUnitName(ingredient.unitID)}
                          </span>
                        </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted">Pas d&apos;ingredients</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <h5 className="text-primary mb-3">Usages</h5>
            {product.usages && product.usages.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {product.usages.map((usage: ProductUsage, index) => (
                  <Card key={index} className="border-0 bg-light rounded-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <Badge bg="success" className="rounded-pill">
                          {usage.cultureName || 'N/A'}
                        </Badge>
                        <Badge bg="warning" text="dark" className="rounded-pill">
                          {usage.pestName || 'N/A'}
                        </Badge>
                      </div>
                      <div className="text-end">
                        <span className="text-muted">Dose: </span>
                        <span className="fw-medium">
                          {usage.doseMin || 0} - {usage.doseMax || 0} {toUnitName(usage.unitID)}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-muted">Pas d&apos;usages</div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};